// src/shared/common/UpdateNotification.tsx
/**
 * .tuMetrics {"tu1": 0.92, "tu2": 0.94, "tu3": 3, "notes": "Update banner with release summary + changelog link", "tuVersion": "2.5"}
 * .help "Cursor-style refresh prompt with optional release summary and link to /updates."
 */
import React from 'react';
import { RefreshCw, X, ScrollText } from 'lucide-react';
import { cn } from '../../utils/cn';
import useUserStore from '@/store/userStore';

interface UpdateNotificationProps {
  onRefresh: () => void;
  onDismiss: () => void;
  onSeeWhatsNew?: () => void;
  mode?: 'major' | 'refresh-only';
  versionLabel?: string | null;
  summary?: string | null;
  updatesHref?: string | null;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  onRefresh,
  onDismiss,
  onSeeWhatsNew,
  mode = 'refresh-only',
  versionLabel,
  summary,
  updatesHref,
}) => {
  const { isDarkMode } = useUserStore();
  const isMajor = mode === 'major';

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 z-[100]',
        'flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg',
        'animate-fade-in-up',
        'max-w-sm',
        isDarkMode ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-200 text-gray-900',
      )}
    >
      <RefreshCw className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-medium">
          {isMajor && versionLabel
            ? `Currents ${versionLabel} is ready`
            : 'Update available'}
        </p>
        {isMajor && summary ? (
          <p className="text-xs opacity-75 leading-relaxed">{summary}</p>
        ) : (
          <p className="text-xs opacity-75 leading-relaxed">
            Refresh to load the latest version.
          </p>
        )}
        {isMajor && updatesHref && (
          <button
            type="button"
            onClick={() => onSeeWhatsNew?.()}
            className={cn(
              'mt-1 inline-flex items-center gap-1 text-xs font-medium',
              isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-500',
            )}
          >
            <ScrollText className="w-3.5 h-3.5" />
            See what&apos;s new
          </button>
        )}
      </div>
      <button
        onClick={onRefresh}
        className={cn(
          'px-3 py-1.5 text-sm font-medium rounded transition-colors flex-shrink-0',
          'hover:scale-105 active:scale-95',
          isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white',
        )}
      >
        Refresh
      </button>
      <button
        onClick={onDismiss}
        className={cn(
          'p-1 rounded-full transition-colors flex-shrink-0',
          'hover:scale-110 active:scale-95',
          isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
        )}
        aria-label="Dismiss for now"
        title="Hide for now — comes back after sleep if you still need to refresh"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default UpdateNotification;
